import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            console.debug("auth check failed — treating as logged out", e?.response?.status || e?.message);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const loginWithCredentials = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        // Security note: JWT stored in localStorage for now. Not vulnerable to
        // CSRF, but XSS-exposed. Planned migration: httpOnly cookies with
        // backend-issued session refresh. Tracked in ROADMAP §P1.
        localStorage.setItem("eden_jwt", data.token);
        setUser(data.user);
        return data.user;
    };

    const signup = async (email, password, name) => {
        const { data } = await api.post("/auth/signup", { email, password, name });
        // See loginWithCredentials for the localStorage / httpOnly-cookie note.
        localStorage.setItem("eden_jwt", data.token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) { console.debug("logout call failed (already logged out?)", e?.message); }
        localStorage.removeItem("eden_jwt");
        setUser(null);
    };

    const refreshUser = checkAuth;

    return (
        <AuthContext.Provider value={{ user, setUser, loading, loginWithCredentials, signup, logout, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
